import { FormattedPrice } from '@10xLabs/react-web/components/FormattedPrice'
import { ChangeEvent, ReactElement, useState } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import { TableContainer, Table, Thead, Tbody, Tr, BorderedTh, BorderedTd } from 'components/v2/Table'
import { safeFormatMessage } from 'utils/intl'
import { EOperationalCostDynamicColumns, IOperationalCostsCollection } from 'features/OperationalCosts/types'
import { OperationalCostVehicleType } from 'gql/generated/graphql'
import Dropdown from 'components/v2/Dropdown'
import { TableColumnsIcon } from 'components/Icons'
import { Checkbox } from 'components/v2/Checkbox'
import { FormattedDistance } from 'components/FormattedDistance'
import { factorDividedBy100 } from 'utils/number'
import { getFromLocalStorage, saveToLocalStorage } from 'utils/local-storage'

const DropdownContent = styled(Dropdown.Content)`
  width: 200px;
  padding: 16px;
`

const CheckboxWrapper = styled.div`
  display: grid;
  gap: 16px 0;
`

const IconContainer = styled.div`
  display: flex;
  cursor: pointer;
`

const StyledTableColumnsIcon = styled(TableColumnsIcon)`
  width: 24px;
  height: 24px;
  fill: #916ee8;
`

const operationCostVehicleTypeMessages = defineMessages({
  [OperationalCostVehicleType.Bus]: {
    id: 'operationalCosts.vehicleType.bus',
    defaultMessage: 'Bus'
  },
  [OperationalCostVehicleType.Van]: {
    id: 'operationalCosts.vehicleType.van',
    defaultMessage: 'Van'
  },
  [OperationalCostVehicleType.Sedan]: {
    id: 'operationalCosts.vehicleType.sedan',
    defaultMessage: 'Sedan'
  },
  [OperationalCostVehicleType.Suv]: {
    id: 'operationalCosts.vehicleType.suv',
    defaultMessage: 'SUV'
  }
})

const OPTINAL_COLUMNS_LIST = [
  EOperationalCostDynamicColumns.businessClient,
  EOperationalCostDynamicColumns.businessSite,
  EOperationalCostDynamicColumns.fixedCost,
  EOperationalCostDynamicColumns.durationCost,
  EOperationalCostDynamicColumns.costPerKm,
  EOperationalCostDynamicColumns.costPerDriver,
  EOperationalCostDynamicColumns.airConditioning,
  EOperationalCostDynamicColumns.baseDistance,
  EOperationalCostDynamicColumns.perKm,
  EOperationalCostDynamicColumns.redZone,
  EOperationalCostDynamicColumns.durationFactor0to4,
  EOperationalCostDynamicColumns.durationFactor5to8,
  EOperationalCostDynamicColumns.durationFactor9to12,
  EOperationalCostDynamicColumns.durationFactor13to16,
  EOperationalCostDynamicColumns.durationFactor17to24
]

const optionalColumnsMessages = defineMessages({
  [EOperationalCostDynamicColumns.businessClient]: {
    id: 'operationalCosts.list.businessClient',
    defaultMessage: 'Business client'
  },
  [EOperationalCostDynamicColumns.businessSite]: {
    id: 'operationalCosts.list.businessSite',
    defaultMessage: 'Business site'
  },
  [EOperationalCostDynamicColumns.fixedCost]: {
    id: 'operationalCosts.list.costs.fixedCost',
    defaultMessage: 'Fixed cost'
  },
  [EOperationalCostDynamicColumns.durationCost]: {
    id: 'operationalCosts.list.costs.durationCost',
    defaultMessage: 'Duration cost'
  },
  [EOperationalCostDynamicColumns.costPerKm]: {
    id: 'operationalCosts.list.costs.costPerKm',
    defaultMessage: 'Cost per km'
  },
  [EOperationalCostDynamicColumns.costPerDriver]: {
    id: 'operationalCosts.list.costs.costPerDriver',
    defaultMessage: 'Cost per driver'
  },
  [EOperationalCostDynamicColumns.airConditioning]: {
    id: 'operationalCosts.list.factors.airConditioning',
    defaultMessage: 'Air conditioning'
  },
  [EOperationalCostDynamicColumns.baseDistance]: {
    id: 'operationalCosts.list.factors.baseDistance',
    defaultMessage: 'Base distance'
  },
  [EOperationalCostDynamicColumns.perKm]: {
    id: 'operationalCosts.list.factors.perKm',
    defaultMessage: 'Per km'
  },
  [EOperationalCostDynamicColumns.redZone]: {
    id: 'operationalCosts.list.factors.redZone',
    defaultMessage: 'Red zone'
  },
  [EOperationalCostDynamicColumns.durationFactor0to4]: {
    id: 'operationalCosts.list.factors.durationFactor0to4',
    defaultMessage: 'Factor (0 - 4 H)'
  },
  [EOperationalCostDynamicColumns.durationFactor5to8]: {
    id: 'operationalCosts.list.factors.durationFactor5to8',
    defaultMessage: 'Factor (5 - 8 H)'
  },
  [EOperationalCostDynamicColumns.durationFactor9to12]: {
    id: 'operationalCosts.list.factors.durationFactor9to12',
    defaultMessage: 'Factor (9 - 12 H)'
  },
  [EOperationalCostDynamicColumns.durationFactor13to16]: {
    id: 'operationalCosts.list.factors.durationFactor13to16',
    defaultMessage: 'Factor (13 - 16 H)'
  },
  [EOperationalCostDynamicColumns.durationFactor17to24]: {
    id: 'operationalCosts.list.factors.durationFactor17to24',
    defaultMessage: 'Factor (17 - 24 H)'
  }
})

interface IOperationalCostsListProps {
  operationalCostsCollection: IOperationalCostsCollection
}

export const OperationalCostsList = ({ operationalCostsCollection }: IOperationalCostsListProps): ReactElement => {
  const intl = useIntl()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [visibleColumns, setVisibleColumns] = useState<EOperationalCostDynamicColumns[]>(() => {
    return getFromLocalStorage<EOperationalCostDynamicColumns[]>('operationalCosts_visibleColumns', [
      EOperationalCostDynamicColumns.fixedCost,
      EOperationalCostDynamicColumns.durationCost,
      EOperationalCostDynamicColumns.costPerKm,
      EOperationalCostDynamicColumns.costPerDriver
    ])
  })

  const handleSelectColumn = (event: ChangeEvent<HTMLInputElement>): void => {
    const id = event.target.id as EOperationalCostDynamicColumns
    const isChecked = event.target.checked
    setVisibleColumns((prev) => {
      const updatedColumns = isChecked ? [...prev, id] : prev.filter((column) => column !== id)
      saveToLocalStorage('operationalCosts_visibleColumns', updatedColumns)
      return updatedColumns
    })
  }

  return (
    <TableContainer overflow>
      <Table>
        <Thead>
          <Tr>
            <BorderedTh noWidth noUppercase>
              <Dropdown.Wrapper onClickAway={(): void => setIsMenuOpen(false)} form>
                <IconContainer onClick={(): void => setIsMenuOpen(!isMenuOpen)}>
                  <StyledTableColumnsIcon />
                </IconContainer>
                <DropdownContent open={isMenuOpen} noIndicator left form noOverflow>
                  <CheckboxWrapper>
                    {OPTINAL_COLUMNS_LIST.map((column): ReactElement => {
                      return (
                        <Checkbox
                          key={`checkbox-${column}`}
                          id={column}
                          checked={visibleColumns.includes(column)}
                          onChange={handleSelectColumn}
                        >
                          {safeFormatMessage({ intl, key: column, messages: optionalColumnsMessages })}
                        </Checkbox>
                      )
                    })}
                  </CheckboxWrapper>
                </DropdownContent>
              </Dropdown.Wrapper>
            </BorderedTh>
            <BorderedTh>
              <FormattedMessage id="operationalCosts.list.stateCode" defaultMessage="State" />
            </BorderedTh>
            <BorderedTh>
              <FormattedMessage id="operationalCosts.list.vehicleType" defaultMessage="Vehicle Type" />
            </BorderedTh>
            {visibleColumns.includes(EOperationalCostDynamicColumns.businessClient) && (
              <BorderedTh>
                {safeFormatMessage({
                  intl,
                  key: EOperationalCostDynamicColumns.businessClient,
                  messages: optionalColumnsMessages
                })}
              </BorderedTh>
            )}
            {visibleColumns.includes(EOperationalCostDynamicColumns.businessSite) && (
              <BorderedTh>
                {safeFormatMessage({
                  intl,
                  key: EOperationalCostDynamicColumns.businessSite,
                  messages: optionalColumnsMessages
                })}
              </BorderedTh>
            )}
            {visibleColumns.includes(EOperationalCostDynamicColumns.fixedCost) && (
              <BorderedTh>
                {safeFormatMessage({
                  intl,
                  key: EOperationalCostDynamicColumns.fixedCost,
                  messages: optionalColumnsMessages
                })}
              </BorderedTh>
            )}
            {visibleColumns.includes(EOperationalCostDynamicColumns.durationCost) && (
              <BorderedTh>
                {safeFormatMessage({
                  intl,
                  key: EOperationalCostDynamicColumns.durationCost,
                  messages: optionalColumnsMessages
                })}
              </BorderedTh>
            )}
            {visibleColumns.includes(EOperationalCostDynamicColumns.costPerKm) && (
              <BorderedTh>
                {safeFormatMessage({
                  intl,
                  key: EOperationalCostDynamicColumns.costPerKm,
                  messages: optionalColumnsMessages
                })}
              </BorderedTh>
            )}
            {visibleColumns.includes(EOperationalCostDynamicColumns.costPerDriver) && (
              <BorderedTh>
                {safeFormatMessage({
                  intl,
                  key: EOperationalCostDynamicColumns.costPerDriver,
                  messages: optionalColumnsMessages
                })}
              </BorderedTh>
            )}
            {visibleColumns.includes(EOperationalCostDynamicColumns.airConditioning) && (
              <BorderedTh>
                {safeFormatMessage({
                  intl,
                  key: EOperationalCostDynamicColumns.airConditioning,
                  messages: optionalColumnsMessages
                })}
              </BorderedTh>
            )}
            {visibleColumns.includes(EOperationalCostDynamicColumns.baseDistance) && (
              <BorderedTh>
                {safeFormatMessage({
                  intl,
                  key: EOperationalCostDynamicColumns.baseDistance,
                  messages: optionalColumnsMessages
                })}
              </BorderedTh>
            )}
            {visibleColumns.includes(EOperationalCostDynamicColumns.perKm) && (
              <BorderedTh>
                {safeFormatMessage({
                  intl,
                  key: EOperationalCostDynamicColumns.perKm,
                  messages: optionalColumnsMessages
                })}
              </BorderedTh>
            )}
            {visibleColumns.includes(EOperationalCostDynamicColumns.redZone) && (
              <BorderedTh>
                {safeFormatMessage({
                  intl,
                  key: EOperationalCostDynamicColumns.redZone,
                  messages: optionalColumnsMessages
                })}
              </BorderedTh>
            )}
            {visibleColumns.includes(EOperationalCostDynamicColumns.durationFactor0to4) && (
              <BorderedTh>
                {safeFormatMessage({
                  intl,
                  key: EOperationalCostDynamicColumns.durationFactor0to4,
                  messages: optionalColumnsMessages
                })}
              </BorderedTh>
            )}
            {visibleColumns.includes(EOperationalCostDynamicColumns.durationFactor5to8) && (
              <BorderedTh>
                {safeFormatMessage({
                  intl,
                  key: EOperationalCostDynamicColumns.durationFactor5to8,
                  messages: optionalColumnsMessages
                })}
              </BorderedTh>
            )}
            {visibleColumns.includes(EOperationalCostDynamicColumns.durationFactor9to12) && (
              <BorderedTh>
                {safeFormatMessage({
                  intl,
                  key: EOperationalCostDynamicColumns.durationFactor9to12,
                  messages: optionalColumnsMessages
                })}
              </BorderedTh>
            )}
            {visibleColumns.includes(EOperationalCostDynamicColumns.durationFactor13to16) && (
              <BorderedTh>
                {safeFormatMessage({
                  intl,
                  key: EOperationalCostDynamicColumns.durationFactor13to16,
                  messages: optionalColumnsMessages
                })}
              </BorderedTh>
            )}
            {visibleColumns.includes(EOperationalCostDynamicColumns.durationFactor17to24) && (
              <BorderedTh>
                {safeFormatMessage({
                  intl,
                  key: EOperationalCostDynamicColumns.durationFactor17to24,
                  messages: optionalColumnsMessages
                })}
              </BorderedTh>
            )}
          </Tr>
        </Thead>
        <Tbody>
          {operationalCostsCollection.items.map((operationalCostModel) => (
            <Tr key={operationalCostModel.id}>
              <BorderedTd />
              <BorderedTd>{operationalCostModel.state.name}</BorderedTd>
              <BorderedTd>
                {safeFormatMessage({
                  intl,
                  messages: operationCostVehicleTypeMessages,
                  key: operationalCostModel.vehicleType
                })}
                ({operationalCostModel.vehicleCapacity})
              </BorderedTd>
              {visibleColumns.includes(EOperationalCostDynamicColumns.businessClient) && (
                <BorderedTd>
                  {operationalCostModel.businessClient ? operationalCostModel.businessClient.name : '-'}
                </BorderedTd>
              )}
              {visibleColumns.includes(EOperationalCostDynamicColumns.businessSite) && (
                <BorderedTd>
                  {operationalCostModel.businessSite ? operationalCostModel.businessSite.name : '-'}
                </BorderedTd>
              )}
              {visibleColumns.includes(EOperationalCostDynamicColumns.fixedCost) && (
                <BorderedTd>
                  <FormattedPrice value={operationalCostModel.settings.costs.fixedCost} />
                </BorderedTd>
              )}
              {visibleColumns.includes(EOperationalCostDynamicColumns.durationCost) && (
                <BorderedTd>
                  <FormattedPrice value={operationalCostModel.settings.costs.durationCost} />
                </BorderedTd>
              )}
              {visibleColumns.includes(EOperationalCostDynamicColumns.costPerKm) && (
                <BorderedTd>
                  <FormattedPrice value={operationalCostModel.settings.costs.costPerKm} />
                </BorderedTd>
              )}
              {visibleColumns.includes(EOperationalCostDynamicColumns.costPerDriver) && (
                <BorderedTd>
                  <FormattedPrice value={operationalCostModel.settings.costs.costPerDriver} />
                </BorderedTd>
              )}
              {visibleColumns.includes(EOperationalCostDynamicColumns.airConditioning) && (
                <BorderedTd>{factorDividedBy100(operationalCostModel.settings.factors.airConditioning)}</BorderedTd>
              )}
              {visibleColumns.includes(EOperationalCostDynamicColumns.baseDistance) && (
                <BorderedTd>
                  <FormattedDistance distance={operationalCostModel.settings.factors.baseDistance} />
                </BorderedTd>
              )}
              {visibleColumns.includes(EOperationalCostDynamicColumns.perKm) && (
                <BorderedTd>{factorDividedBy100(operationalCostModel.settings.factors.perKm)}</BorderedTd>
              )}
              {visibleColumns.includes(EOperationalCostDynamicColumns.redZone) && (
                <BorderedTd>{factorDividedBy100(operationalCostModel.settings.factors.redZone)}</BorderedTd>
              )}
              {visibleColumns.includes(EOperationalCostDynamicColumns.durationFactor0to4) && (
                <BorderedTd>{factorDividedBy100(operationalCostModel.settings.factors.durationFactor0to4)}</BorderedTd>
              )}
              {visibleColumns.includes(EOperationalCostDynamicColumns.durationFactor5to8) && (
                <BorderedTd>{factorDividedBy100(operationalCostModel.settings.factors.durationFactor5to8)}</BorderedTd>
              )}
              {visibleColumns.includes(EOperationalCostDynamicColumns.durationFactor9to12) && (
                <BorderedTd>{factorDividedBy100(operationalCostModel.settings.factors.durationFactor9to12)}</BorderedTd>
              )}
              {visibleColumns.includes(EOperationalCostDynamicColumns.durationFactor13to16) && (
                <BorderedTd>
                  {factorDividedBy100(operationalCostModel.settings.factors.durationFactor13to16)}
                </BorderedTd>
              )}
              {visibleColumns.includes(EOperationalCostDynamicColumns.durationFactor17to24) && (
                <BorderedTd>
                  {factorDividedBy100(operationalCostModel.settings.factors.durationFactor17to24)}
                </BorderedTd>
              )}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  )
}
